import { Code, Eye, LayoutGrid, Move, PenLine, Plus } from 'lucide-react';

export type BlocksViewMode = 'edit' | 'preview' | 'json';

const MODE_META: Record<
  BlocksViewMode,
  { label: string; hint: string; Icon: typeof PenLine }
> = {
  edit: {
    label: 'Edit',
    hint: 'Edit block source. Double-click a block for fullscreen editing; Edit opens in place. Turn on Reorder to drag blocks.',
    Icon: PenLine,
  },
  preview: {
    label: 'Preview',
    hint: 'Server-rendered send preview (same pipeline as materialize). Postmark templates use sample merge data.',
    Icon: Eye,
  },
  json: {
    label: 'JSON',
    hint: 'Edit the raw content array. Add content updates JSON here; use Edit for block-by-block layout.',
    Icon: Code,
  },
};

interface NewsletterBlocksToolbarProps {
  blocksCount: number;
  blocksViewMode: BlocksViewMode;
  onBlocksViewModeChange: (mode: BlocksViewMode) => void;
  showBoundaries: boolean;
  onShowBoundariesChange: (show: boolean) => void;
  reorderEnabled: boolean;
  onReorderEnabledChange: (enabled: boolean) => void;
  onAddContent: () => void;
}

export default function NewsletterBlocksToolbar({
  blocksCount,
  blocksViewMode,
  onBlocksViewModeChange,
  showBoundaries,
  onShowBoundariesChange,
  reorderEnabled,
  onReorderEnabledChange,
  onAddContent,
}: NewsletterBlocksToolbarProps) {
  const modeHint = MODE_META[blocksViewMode].hint;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Content
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">({blocksCount})</span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div
            className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-900/50"
            role="tablist"
            aria-label="Content view mode"
          >
            {(Object.keys(MODE_META) as BlocksViewMode[]).map((mode) => {
              const { label, Icon } = MODE_META[mode];
              return (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={blocksViewMode === mode}
                  onClick={() => onBlocksViewModeChange(mode)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    blocksViewMode === mode
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              );
            })}
          </div>

          {blocksViewMode === 'edit' ? (
            <>
              <button
                type="button"
                onClick={() => onReorderEnabledChange(!reorderEnabled)}
                aria-pressed={reorderEnabled}
                aria-label={
                  reorderEnabled ? 'Disable drag to reorder blocks' : 'Enable drag to reorder blocks'
                }
                title={
                  reorderEnabled
                    ? 'Reorder on — drag blocks to move. Turn off to select text while editing.'
                    : 'Enable reorder — drag blocks up or down'
                }
                className={`inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  reorderEnabled
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/25 dark:text-emerald-100 dark:border-emerald-600'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/40'
                }`}
              >
                <Move className="w-4 h-4" />
                Reorder
              </button>
              <button
                type="button"
                onClick={() => onShowBoundariesChange(!showBoundaries)}
                aria-pressed={showBoundaries}
                aria-label={showBoundaries ? 'Hide block labels and borders' : 'Show block labels and borders'}
                title={showBoundaries ? 'Hide block chrome' : 'Show block chrome'}
                className={`inline-flex items-center justify-center p-2.5 rounded-lg border transition-colors ${
                  showBoundaries
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/25 dark:text-emerald-100 dark:border-emerald-600'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/40'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </>
          ) : null}
          <button
            type="button"
            onClick={onAddContent}
            disabled={blocksViewMode === 'preview'}
            title={
              blocksViewMode === 'preview'
                ? 'Switch to Edit or JSON to add content'
                : 'Add a blank block or insert from the element library'
            }
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Add content
          </button>
        </div>
      </div>

      <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">{modeHint}</p>
    </div>
  );
}
